import { Gap, ISelectMultiOption, MainArea, Page, Tab, TabBar, Tabs, TopArea } from '@salecycle/ui-component-library';
import AppTopBar from 'core/ui/components/AppTopBar/AppTopBar';
import { FC, useEffect, useState } from 'react';
import { BlocklistAllowlistHeading } from '../components/BlocklistAllowlistHeading';
import { useLocation, useParams } from 'react-router';
import ipRegex from 'ip-regex';
import {
  IBlocklistAllowlistItem,
  listTypeAllowlist,
  recordTypeEmail,
  listTypeBlocklist,
  recordTypeIPAddress,
  recordTypeDomain,
  channelEmail,
  channelAll,
  recordTypeMobile,
} from '../entities/blocklist-allowlist-constants';
import BlocklistAllowlistDataDisplay from '../components/BlocklistAllowlistDataDisplay';
import { useBlocklistAllowlist } from '../components/hooks/useBlocklistAllowlist';
import { listOfStringsToMultiSelectOptions } from 'components/utils/getSelectOptions';

const emailTab = 'Email addresses';
const smsTab = 'Mobile numbers';

const tabIds = [emailTab, smsTab];
export type TabState = typeof tabIds[number];

export type invalidInputs = Map<string, string[]>;

export const BlocklistAllowlistPage: FC = () => {
  const { pathname } = useLocation();
  const { siteId } = useParams<{ siteId: string }>();

  const [activeTabId, setActiveTabId] = useState<TabState>('Email addresses');

  const [emailAddress, setEmailAddress] = useState<ISelectMultiOption<string>[]>([]);
  const [emailName, setEmailName] = useState('');

  const [mobileNumber, setMobileNumber] = useState<ISelectMultiOption<string>[]>([]);
  const [mobileName, setMobileName] = useState('');

  const invalidEmailsMap = new Map<string, string[]>();
  invalidEmailsMap.set('Invalid', []);

  const invalidMobilesMap = new Map<string, string[]>();
  invalidMobilesMap.set('LengthTooLong', []);
  invalidMobilesMap.set('LengthTooShort', []);
  invalidMobilesMap.set('NoPlusSign', []);
  invalidMobilesMap.set('SpecialCharacters', []);

  const [invalidEmails, setInvalidEmails] = useState<invalidInputs>(invalidEmailsMap);
  const [duplicateEmails, setDuplicateEmails] = useState<string[]>([]);

  const [invalidMobiles, setInvalidMobiles] = useState<invalidInputs>(invalidMobilesMap);
  const [duplicateMobiles, setDuplicateMobiles] = useState<string[]>([]);

  const emailAddressChangeHandler = (value: ISelectMultiOption<string>[]) => {
    setEmailAddress(value);
  };

  const emailNameChangeHandler = (value: string) => {
    setEmailName(value);
  };

  const mobileNumberChangeHandler = (value: ISelectMultiOption<string>[]) => {
    setMobileNumber(value);
  };

  const mobileNameChangeHandler = (value: string) => {
    setMobileName(value);
  };

  const [ipAddress, setIpAddress] = useState<ISelectMultiOption<string>[]>([]);
  const [ipAddressName, setIpAddressName] = useState('');

  const invalidIpAddressMap = new Map<string, string[]>();
  invalidIpAddressMap.set('Invalid', []);

  const [invalidIpAddress, setInvalidIpAddress] = useState<invalidInputs>(invalidIpAddressMap);
  const [duplicateIpAddress, setDuplicateIpAddress] = useState<string[]>([]);

  const ipAddressChangeHandler = (value: ISelectMultiOption<string>[]) => {
    setIpAddress(value);
  };

  const ipAddressNameChangeHandler = (value: string) => {
    setIpAddressName(value);
  };

  const [domain, setDomain] = useState<ISelectMultiOption<string>[]>([]);
  const [domainName, setDomainName] = useState('');

  const invalidDomainsMap = new Map<string, string[]>();
  invalidDomainsMap.set('Invalid', []);
  const [invalidDomains, setInvalidDomains] = useState<invalidInputs>(invalidDomainsMap);
  const [duplicateDomains, setDuplicateDomains] = useState<string[]>([]);

  const domainChangeHandler = (value: ISelectMultiOption<string>[]) => {
    setDomain(value);
  };

  const domainNameChangeHandler = (value: string) => {
    setDomainName(value);
  };

  const [allowlistEmailData, setAllowlistEmailData] = useState<IBlocklistAllowlistItem[]>(
    [] as IBlocklistAllowlistItem[],
  );
  const [allowlistMobileData, setAllowlistMobileData] = useState<IBlocklistAllowlistItem[]>(
    [] as IBlocklistAllowlistItem[],
  );

  const [blocklistIPData, setBlocklistIPData] = useState<IBlocklistAllowlistItem[]>([] as IBlocklistAllowlistItem[]);
  const [blocklistDomainData, setBlocklistDomainData] = useState<IBlocklistAllowlistItem[]>(
    [] as IBlocklistAllowlistItem[],
  );

  let queryString = '';
  if (pathname.endsWith(listTypeBlocklist)) {
    queryString = `productType=${channelEmail}&listType=${listTypeBlocklist}`;
  }
  if (pathname.endsWith(listTypeAllowlist)) {
    queryString = `productType=${channelAll}&listType=${listTypeAllowlist}`;
  }
  const { data, isSuccess, isLoading } = useBlocklistAllowlist(siteId, queryString);

  const filterByItemType: any = (data: IBlocklistAllowlistItem[], itemType: string) => {
    return data.filter(item => {
      return item.type === itemType;
    });
  };

  useEffect(() => {
    if (pathname.endsWith(listTypeBlocklist) && isSuccess) {
      setBlocklistIPData(data.filter(blocklistIPItem => blocklistIPItem.type === recordTypeIPAddress));
      setBlocklistDomainData(data.filter(blocklistDomainItem => blocklistDomainItem.type === recordTypeDomain));
    }
    if (pathname.endsWith(listTypeAllowlist) && isSuccess) {
      setAllowlistEmailData(filterByItemType(data, recordTypeEmail));
      setAllowlistMobileData(filterByItemType(data, recordTypeMobile));
    }
  }, [data, isSuccess, pathname]);

  const addBlocklistAllowlistCallback = (newItems: IBlocklistAllowlistItem[]) => {
    newItems.forEach(newItem => {
      if (newItem.listType === listTypeAllowlist && newItem.type === recordTypeEmail) {
        setAllowlistEmailData(allowlistEmailData => [newItem as IBlocklistAllowlistItem, ...allowlistEmailData]);
      }
      if (newItem.listType === listTypeAllowlist && newItem.type === recordTypeMobile) {
        setAllowlistMobileData(allowlistMobileData => [newItem as IBlocklistAllowlistItem, ...allowlistMobileData]);
      }
      if (newItem.listType === listTypeBlocklist && newItem.type === recordTypeIPAddress) {
        setBlocklistIPData(blocklistIPData => [newItem as IBlocklistAllowlistItem, ...blocklistIPData]);
      }
      if (newItem.listType === listTypeBlocklist && newItem.type === recordTypeDomain) {
        setBlocklistDomainData(blocklistDomainData => [newItem as IBlocklistAllowlistItem, ...blocklistDomainData]);
      }
    });
  };

  const deleteBlocklistAllowlistCallback = (itemKey: string, listType: string, recordType: string) => {
    if (listType === listTypeAllowlist && recordType === recordTypeEmail) {
      setAllowlistEmailData(allowlistEmailData.filter(allowlistItem => allowlistItem.itemKey !== itemKey));
    }
    if (listType === listTypeAllowlist && recordType === recordTypeMobile) {
      setAllowlistMobileData(allowlistMobileData.filter(allowlistItem => allowlistItem.itemKey !== itemKey));
    }
    if (listType === listTypeBlocklist && recordType === recordTypeIPAddress) {
      setBlocklistIPData(blocklistIPData.filter(blocklistIPItem => blocklistIPItem.itemKey !== itemKey));
    }
    if (listType === listTypeBlocklist && recordType === recordTypeDomain) {
      setBlocklistDomainData(
        blocklistDomainData.filter(blocklistDomainItem => blocklistDomainItem.itemKey !== itemKey),
      );
    }
  };

  const splitValues = (inputValue: string) => {
    let splitNewItemValues: ISelectMultiOption<string>[] = [];
    splitNewItemValues = listOfStringsToMultiSelectOptions(inputValue.split(/[,\s]+/).map(item => item.trim()));
    return splitNewItemValues;
  };

  const checkIfItemIsAlreadySavedOrEntered = (
    input: string,
    savedData: IBlocklistAllowlistItem[],
    enteredData: ISelectMultiOption<string>[],
    valueType: string,
  ) => {
    let isSavedOrEntered = false;
    if (savedData && savedData.length > 0) {
      savedData.forEach(x => {
        isSavedOrEntered = checkValueMatches(valueType, input, x, isSavedOrEntered);
      });
    }
    if (!isSavedOrEntered) {
      if (enteredData && enteredData.length > 0) {
        enteredData.forEach(x => {
          isSavedOrEntered = checkValueMatches(valueType, input, x, isSavedOrEntered);
        });
      }
    }
    return isSavedOrEntered;
  };

  const checkValueMatches = (valueType, input, x, isSavedOrEntered) => {
    if (valueType === 'IP') {
      if (input === x.value) {
        isSavedOrEntered = true;
      }
    } else {
      if (input.toLowerCase() === x.value.toLowerCase()) {
        isSavedOrEntered = true;
      }
    }
    return isSavedOrEntered;
  };

  const addBlocklistIpAddressToSelectMulti = (inputValue: string) => {
    const ipsInvalid: invalidInputs = new Map<string, string[]>();
    ipsInvalid.set('Invalid', []);
    const ipsDuplicate: string[] = [];
    const ips: ISelectMultiOption<string>[] = [];
    setInvalidIpAddress(new Map<string, string[]>());
    setDuplicateIpAddress([]);
    splitValues(inputValue.trim()).forEach(x => {
      if (ipRegex({ exact: true }).test(x.value)) {
        if (checkIfItemIsAlreadySavedOrEntered(x.value, blocklistIPData, ipAddress, 'IP')) {
          ipsDuplicate.push(x.value);
        } else {
          if (ipAddress === null) ips.push();
          else ipAddress.push(x);
        }
      } else {
        ipsInvalid.get('Invalid').push(x.value);
      }
    });
    setInvalidIpAddress(ipsInvalid);
    setDuplicateIpAddress(ipsDuplicate);
    if (ipAddress === null) setIpAddress(ips);
    else setIpAddress(ipAddress);
  };

  const addBlocklistDomainToSelectMulti = (inputValue: string) => {
    const domainsInvalid: invalidInputs = new Map<string, string[]>();
    domainsInvalid.set('Invalid', []);
    const domainsDuplicate: string[] = [];
    const domains: ISelectMultiOption<string>[] = [];
    setInvalidDomains(new Map<string, string[]>());
    setDuplicateDomains([]);
    splitValues(inputValue.trim()).forEach(x => {
      if (/^[a-zA-Z0-9](?:[\w-]*[a-zA-Z0-9]\.)+[a-zA-Z]{2,63}$/.test(x.value)) {
        if (checkIfItemIsAlreadySavedOrEntered(x.value, blocklistDomainData, domain, 'DOMAIN')) {
          domainsDuplicate.push(x.value);
        } else {
          if (domain === null) domains.push(x);
          else domain.push(x);
        }
      } else {
        domainsInvalid.get('Invalid').push(x.value);
      }
    });
    if (domain === null) setDomain(domains);
    else setDomain(domain);
    setInvalidDomains(domainsInvalid);
    setDuplicateDomains(domainsDuplicate);
  };

  const addAllowlistEmailToSelectMulti = (inputValue: string) => {
    const emailsInvalid: Map<string, string[]> = new Map<string, string[]>();
    emailsInvalid.set('Invalid', []);
    const emailsDuplicate: string[] = [];
    const emails: ISelectMultiOption<string>[] = [];
    setInvalidEmails(new Map<string, string[]>());
    setDuplicateEmails([]);
    splitValues(inputValue.trim()).forEach(x => {
      if (/\S+@\S+\.\S{2,}/.test(x.value)) {
        if (checkIfItemIsAlreadySavedOrEntered(x.value, allowlistEmailData, emailAddress, 'EMAIL')) {
          emailsDuplicate.push(x.value);
        } else {
          if (emailAddress === null) emails.push(x);
          else emailAddress.push(x);
        }
      } else {
        emailsInvalid.get('Invalid').push(x.value);
      }
    });
    if (emailAddress === null) setEmailAddress(emails);
    else setEmailAddress(emailAddress);
    setInvalidEmails(emailsInvalid);
    setDuplicateEmails(emailsDuplicate);
  };

  const addAllowlistMobileToSelectMulti = (inputValue: string) => {
    const mobilesInvalid: Map<string, string[]> = new Map<string, string[]>();
    mobilesInvalid.set('LengthTooLong', []);
    mobilesInvalid.set('LengthTooShort', []);
    mobilesInvalid.set('NoPlusSign', []);
    mobilesInvalid.set('SpecialCharacters', []);
    const mobilesDuplicate: string[] = [];
    const mobiles: ISelectMultiOption<string>[] = [];
    setInvalidMobiles(new Map<string, string[]>());
    setDuplicateMobiles([]);
    splitValues(inputValue.trim()).forEach(x => {
      let isValid = true;

      // find + sign
      const plusSign = x.value.indexOf('+');

      // MUST include a plus sign at the start
      if (plusSign !== 0) {
        isValid = false;
        mobilesInvalid.get('NoPlusSign').push(x.value);
      }

      // Rest of input MUST be only numbers
      if (!/^[\d\s]+$/.test(x.value.substring(1))) {
        isValid = false;
        mobilesInvalid.get('SpecialCharacters').push(x.value);
      }

      // Must be AT LEAST 6 characters
      if (x.value.length < 6) {
        isValid = false;
        mobilesInvalid.get('LengthTooShort').push(x.value);
      }

      // Must be AT MOST 15 characters
      if (x.value.length > 15) {
        isValid = false;
        mobilesInvalid.get('LengthTooLong').push(x.value);
      }

      if (isValid) {
        if (checkIfItemIsAlreadySavedOrEntered(x.value, allowlistMobileData, mobileNumber, 'MOBILE')) {
          mobilesDuplicate.push(x.value);
        } else {
          if (mobileNumber === null) {
            mobiles.push(x);
          } else {
            mobileNumber.push(x);
          }
        }
      }
    });

    if (mobileNumber === null) {
      setMobileNumber(mobiles);
    } else {
      setMobileNumber(mobileNumber);
    }
    setInvalidMobiles(mobilesInvalid);
    setDuplicateMobiles(mobilesDuplicate);
  };

  if (pathname.endsWith(listTypeAllowlist)) {
    return (
      <MainArea>
        <TopArea>
          <AppTopBar />
        </TopArea>
        <Page data-test-id="AllowListForm">
          <BlocklistAllowlistHeading listType={listTypeAllowlist} />
          <Tabs fillHeight={false}>
            <TabBar borderVariation="clean">
              {tabIds.map((id: TabState) => (
                <Tab
                  tabId={id}
                  key={id}
                  padding="0px 24px 16px 24px"
                  onClick={() => {
                    setActiveTabId(id);
                  }}
                >
                  {id}
                </Tab>
              ))}
            </TabBar>
          </Tabs>

          {activeTabId === smsTab ? (
            <>
              <BlocklistAllowlistDataDisplay
                dataToDisplay={allowlistMobileData}
                addBlocklistAllowlistCallback={addBlocklistAllowlistCallback}
                addItemValueToSelectMulti={addAllowlistMobileToSelectMulti}
                deleteBlocklistAllowlistCallback={deleteBlocklistAllowlistCallback}
                isLoading={isLoading}
                itemName={mobileName}
                itemNameChangeHandler={mobileNameChangeHandler}
                itemValue={mobileNumber}
                invalidItemValues={invalidMobiles}
                duplicateItemValues={duplicateMobiles}
                setInvalidItemValues={setInvalidMobiles}
                setDuplicateItemValues={setDuplicateMobiles}
                itemValueChangeHandler={mobileNumberChangeHandler}
                listType={listTypeAllowlist}
                valueType={recordTypeMobile}
                setItemName={setMobileName}
                setItemValue={setMobileNumber}
              />
            </>
          ) : (
            <>
              <BlocklistAllowlistDataDisplay
                dataToDisplay={allowlistEmailData}
                addBlocklistAllowlistCallback={addBlocklistAllowlistCallback}
                addItemValueToSelectMulti={addAllowlistEmailToSelectMulti}
                deleteBlocklistAllowlistCallback={deleteBlocklistAllowlistCallback}
                isLoading={isLoading}
                itemName={emailName}
                itemNameChangeHandler={emailNameChangeHandler}
                itemValue={emailAddress}
                invalidItemValues={invalidEmails}
                duplicateItemValues={duplicateEmails}
                setInvalidItemValues={setInvalidEmails}
                setDuplicateItemValues={setDuplicateEmails}
                itemValueChangeHandler={emailAddressChangeHandler}
                listType={listTypeAllowlist}
                valueType={recordTypeEmail}
                setItemName={setEmailName}
                setItemValue={setEmailAddress}
              />
            </>
          )}
        </Page>
      </MainArea>
    );
  }
  if (pathname.endsWith(listTypeBlocklist)) {
    return (
      <MainArea>
        <TopArea>
          <AppTopBar />
        </TopArea>
        <Page data-test-id="BlockListForm">
          <BlocklistAllowlistHeading listType={listTypeBlocklist} />
          <BlocklistAllowlistDataDisplay
            dataToDisplay={blocklistIPData}
            addBlocklistAllowlistCallback={addBlocklistAllowlistCallback}
            addItemValueToSelectMulti={addBlocklistIpAddressToSelectMulti}
            deleteBlocklistAllowlistCallback={deleteBlocklistAllowlistCallback}
            isLoading={isLoading}
            itemName={ipAddressName}
            itemNameChangeHandler={ipAddressNameChangeHandler}
            itemValue={ipAddress}
            invalidItemValues={invalidIpAddress}
            duplicateItemValues={duplicateIpAddress}
            setInvalidItemValues={setInvalidIpAddress}
            setDuplicateItemValues={setDuplicateIpAddress}
            itemValueChangeHandler={ipAddressChangeHandler}
            listType={listTypeBlocklist}
            valueType={recordTypeIPAddress}
            setItemName={setIpAddressName}
            setItemValue={setIpAddress}
          />
          <Gap bottom={100} />
          <BlocklistAllowlistDataDisplay
            dataToDisplay={blocklistDomainData}
            addBlocklistAllowlistCallback={addBlocklistAllowlistCallback}
            addItemValueToSelectMulti={addBlocklistDomainToSelectMulti}
            deleteBlocklistAllowlistCallback={deleteBlocklistAllowlistCallback}
            isLoading={isLoading}
            itemName={domainName}
            itemNameChangeHandler={domainNameChangeHandler}
            itemValue={domain}
            invalidItemValues={invalidDomains}
            duplicateItemValues={duplicateDomains}
            setInvalidItemValues={setInvalidDomains}
            setDuplicateItemValues={setDuplicateDomains}
            itemValueChangeHandler={domainChangeHandler}
            listType={listTypeBlocklist}
            valueType={recordTypeDomain}
            setItemName={setDomainName}
            setItemValue={setDomain}
          />
        </Page>
      </MainArea>
    );
  }
};
